import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ICustomer } from '../../interfaces/ICustomer';
import { useCustomerService } from '../../services/useCustomerService';
import { useToastr } from '../../hooks/useToastr';
import IFormError from '../../interfaces/IFormError';
import { useValidateSchema } from '../../services/useValidateSchema';
import * as yup from 'yup';
import { IOrder } from '../../interfaces/IOrder';
import { useOrderService } from '../../services/useOrderService';
import AsyncSearch, { IOption } from '../../components/async-search/AsyncSearch';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import validateUuId from '../../util/validateUuid';
import moment from 'moment';
import IOrderCustomerFormProps from './interfaces/IOrderCustomerFormProps';
import { useAuth } from '../../hooks/auth';
import { ISeller } from '../../interfaces/ISeller';
import { useSellersService } from '../../services/useSellersService';
import { IBranchOffice } from '../../interfaces/IBranchOffice';
import { Autocomplete, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import CustomerDialogForm from './CustomerDialogForm';
import { useHubSpotService } from '../../services/useHubSpotService';
import { BackdropCustom } from '../../components/backdrop/Backdrop';
import { ButtonCustom } from '../../components/confirmation-area/ConfirmationArea.styled';
import IOrderType from '../../interfaces/IOrderType';
import { useOrderTypesService } from '../../services/useOrderTypesService';
import { useSessionStorage } from '../../services/useSessionStorage';
import { useBranchOfficeService } from '../../services/useBranchOfficeService';

interface IFormErrorResult {
  errors: IFormError | null;
  isValid: boolean;
}

const OrderCustomerForm = forwardRef<IOrderCustomerFormProps, {}>((props, ref) => {
  const timeout = useRef<any>(0);

  const customerService = useCustomerService();
  const sellerService = useSellersService();
  const orderService = useOrderService();
  const toastr = useToastr();
  const validation = useValidateSchema();
  const navigate = useNavigate();
  const params = useParams();
  const { validateHubSpotDealId } = useHubSpotService();
  const location = useLocation();
  const { setItem } = useSessionStorage();
  const branchOfficeService = useBranchOfficeService();

  const orderTypeService = useOrderTypesService();

  const { state, orderParams } = useAuth();

  useImperativeHandle(ref, () => ({
    listOrder,
    async save() {
      return handleSave();
    },
    clearForm,
  }));

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = React.useState<ICustomer[]>([]);
  const [customerOptions, setCustomerOptions] = React.useState<IOption[]>([]);
  const [customerOption, setCustomerOption] = React.useState<IOption | null>(null);
  const [isCustomerDialogOpen, setIsCustomerDialogOpen] = useState(false);
  const [customerGroupId, setCustomerGroupId] = React.useState<string | null>(null);

  const [branchOffice, setBranchOffice] = React.useState<IBranchOffice | null>(() => {
    return orderParams?.defaultBranchOffice || null;
  });

  const [errors, setErrors] = useState<IFormError | null>();
  const [perPage] = useState(50);
  const [currentPage] = useState(0);
  const [orderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField] = useState<string>('corporateName');
  const [customer, setCustomer] = useState<ICustomer | null>(null);
  const [customerDefault, setCustomerDefault] = useState<ICustomer | null>(null);
  const [seller, setSeller] = useState<ISeller | null>(null);
  const [sellers, setSellers] = React.useState<ISeller[]>([]);
  const [errorsSeller, setErrorsSeller] = useState<IFormError | null>();
  const [sellerOptions, setSellerOptions] = React.useState<IOption[]>([]);
  const [sellerOption, setSellerOption] = React.useState<IOption | null>(null);
  const [pipeline, setPipeline] = useState<string>('');
  const [order, setOrder] = useState<IOrder | null>(null);
  const [integrateHubSpot] = useState(() => {
    return orderParams?.integrateHubSpot;
  });
  const [hubSpotId, setHubSpotId] = useState('');
  const [errorsHubSpot, setErrorsHubSpot] = useState<IFormError | null>();
  const [enableProspect] = useState(() => {
    return orderParams?.enableProspect;
  });
  const [orderParamsLoading] = useState(false);
  const [validateHubSpotDealIdLoading, setValidateHubSpotDealIdLoading] = useState(false);
  const [hubSpotDealIdIsValid, setHubSpotDealIdIsValid] = useState(false);
  const [isNew] = useState(() => {
    return location.pathname.includes('/new');
  });
  const [orderType, setOrderType] = useState<IOrderType | null>(null);
  const [orderTypes, setOrderTypes] = useState<IOrderType[]>([]);
  const [branchOffices, setBranchOffices] = useState<IBranchOffice[]>([]);

  React.useEffect(() => {
    if (!orderParams?.integrateHubSpot) {
      handleListCustomersDynamically();
    }
    listSellers();
  }, [customer, seller, params.id]);

  const adminResource = state.resources.find(value => value === 'administrador-pedido-web');

  const schemaHubSpot = yup.object({
    orderTypeId: yup.number().required('Campo obrigatório').min(1, 'Campo obrigatório.'),
    hubSpotId: yup.string().required('Campo obrigatório').min(1, 'Campo obrigatório.'),
  });

  const schemaSeller = yup.object({
    name: yup.string().required('Campo obrigatório').min(1, 'Campo obrigatório.'),
  });

  const schema = yup.object({
    value: yup.string().required('Campo obrigatório').min(36, 'Campo obrigatório.'),
  });

  const handleSave = async () => {
    if (params.id && validateUuId(params.id)) {
      return await updateOrder();
    } else {
      return await createOrder();
    }
  };

  const updateOrder = async () => {
    const resultCustomer = await validation.validate(schema, customerOption || {});
    setErrors(resultCustomer.errors);

    const resultSeller = await validation.validate(schemaSeller, seller || {});
    setErrorsSeller(resultSeller.errors);

    if (!adminResource) {
      resultSeller.isValid = true;
    }

    if (resultCustomer.isValid && resultSeller.isValid) {
      const sellerId = customer?.sellersCustomer?.find(() => true)?.sellerId || state.seller?.id || seller?.id;

      if (!branchOffice?.id) {
        toastr.error('Não foi localizada uma filial para incluão do pedido.');
        return false;
      }

      if (!sellerId) {
        toastr.error('Cliente não possui um representante vinculado! Por favor revise os cadastros.');
        return false;
      }

      const order: IOrder = {
        id: params.id,
        branchOfficeId: branchOffice.id,
        orderNumber: '0',
        orderTypeId: orderType?.id || 1,
        customerId: `${customerOption?.value}`,
        sellerId,
        pipeline,
      };

      setLoading(true);
      return await orderService
        .updateOrder(`${order.id}`, order)
        .then(result => {
          return true;
        })
        .catch(error => {
          toastr.error(error.message);
          return false;
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      return false;
    }
  };

  const createOrder = async () => {
    const resultCustomer = await validation.validate(schema, customerOption || {});
    setErrors(resultCustomer.errors);

    const resultSeller = await validation.validate(schemaSeller, seller || {});
    setErrorsSeller(resultSeller.errors);

    let resultHubSpot: IFormErrorResult | null = null;
    if (integrateHubSpot) {
      const data = { hubSpotId, orderTypeId: orderType?.id };

      resultHubSpot = await validation.validate(schemaHubSpot, data || {});

      setErrorsHubSpot(resultHubSpot.errors);
    }

    if (!adminResource) {
      resultSeller.isValid = true;
    }

    if (resultCustomer.isValid && resultSeller.isValid && (resultHubSpot?.isValid || !resultHubSpot)) {
      const sellerId = customer?.sellersCustomer?.find(() => true)?.sellerId || state.seller?.id || seller?.id;
      if (!branchOffice?.id) {
        toastr.error('Não foi localizada uma filial para incluão do pedido.');
        return false;
      }

      if (!sellerId) {
        toastr.error('Cliente não possui um representante vinculado! Por favor revise os cadastros.');
        return false;
      }

      const order: IOrder = {
        orderDate: moment().format('yyyy-MM-DD'),
        branchOfficeId: branchOffice.id,
        orderNumber: '0',
        orderTypeId: orderType?.id || 1,
        orderStatusId: 1, // orçamento
        customerId: `${customerOption?.value}`,
        sellerId,
        hubSpotId,
        pipeline,
      };

      setLoading(true);
      return await orderService
        .createOrder(order)
        .then(result => {
          navigate(`/order/steper/${result.id}/edit`);
          return true;
        })
        .catch(error => {
          toastr.error(error.message);
          return false;
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      return false;
    }
  };

  const listOrder = async (id: string) => {
    setLoading(true);
    await orderService
      .listOrder(id, true)
      .then(result => {
        result && setOrder({ ...result });
        if (result && result.customer && result.seller) {
          setCustomer(result.customer);
          setCustomerGroupId(result?.customer?.customerGroupId || null);
          setItem('PEDIDO-WEB:CUSTOMER_ID', result.customer.id);
          setCustomerOption(customerToOption(result.customer));

          const options: IOption[] = [customerToOption(result.customer)];
          setCustomerOptions(options);
          setCustomerDefault(result.customer);

          setSeller(result.seller);
          setSellerOption(sellerToOption(result.seller));

          setHubSpotId(result.hubSpotId || '');
          setOrderType(result.orderType || null);
          setBranchOffice(result.branchOffice || null);
        }
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const listOrderTypes = useCallback(async () => {
    const url = `perPage=1000&currentPage=1&orderBy=description&orderDirection=ASC`;

    setLoading(true);
    await orderTypeService
      .findAll(url)
      .then(result => {
        if (result) {
          setOrderTypes(result.data.filter(value => value.isActive === true));

          console.log(orderType);
          const type = result.data.find(value => value.id === 1);
          console.log(type);
          if (type && orderType === null) {
            setOrderType(type);
          }
        } else {
          setOrderTypes([]);
        }
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [orderType, toastr]);

  const handleSetSeller = (option: IOption | null) => {
    if (option) {
      setSellerOption(option);
      const selected = sellers.find(customer => {
        return customer.id === option.value;
      });
      if (selected?.code) {
        setSeller(selected);
      } else {
        setSeller(null);
      }
    }
  };

  const sellerToOption = (seller: ISeller): IOption => {
    return {
      value: `${seller.id}`,
      description: `${seller.code} ${seller.name}`,
    };
  };

  const listSellers = async (filter = '') => {
    clearInterval(timeout.current);
    timeout.current = setTimeout(async () => {
      setLoading(true);
      const url = `perPage=100&currentPage=1&orderBy=name&orderDirection=asc&filter=${filter}`;

      await sellerService
        .listDynamically(url)
        .then(result => {
          if (result) {
            setSellers(result.data);
            const options: IOption[] = result.data.map(sellerToOption);
            const isSallerOnArray = options.find(opt => opt.value === seller?.id);
            if (seller?.id && !isSallerOnArray) {
              options.push(sellerToOption(seller));
            }
            setSellerOptions(options);
          } else {
            setSellerOptions([]);
          }
        })
        .catch(error => {
          setSellerOptions([]);
          toastr.error(error.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }, timeout.current);
  };

  const handleSetCustomer = (option: IOption | null) => {
    if (option) {
      setCustomerOption(option);
      const selected = customers.find(customer => {
        return customer.id === option.value;
      });
      if (selected?.id) {
        setCustomer(selected);
      } else {
        setCustomer(null);
      }
    }
  };

  const customerToOption = (customer: ICustomer): IOption => {
    let description = `${customer.code} ${customer.corporateName} ${customer.corporateDocument} ${
      customer.customerLevel ? '- ' + customer.customerLevel.description : ''
    }`;
    if (customer.isProspect) {
      description = `(Prospect) ${customer.corporateName} ${customer.corporateDocument} ${
        customer.customerLevel ? '- ' + customer.customerLevel.description : ''
      }`;
    }
    return {
      value: `${customer.id}`,
      description,
    };
  };

  const listAllCustomers = useCallback(async (url: string) => {
    setLoading(true);
    await customerService
      .listDynamically(url)
      .then(result => {
        if (result) {
          setCustomers(result.data);
          const options: IOption[] = result.data.map(customerToOption);
          const isCustomerOnArray = options.find(opt => opt.value === customer?.id);
          if (customer?.id && !isCustomerOnArray) {
            options.push(customerToOption(customer));
          }
          setCustomerOptions(options);
        } else {
          setCustomerOptions([]);
        }
      })
      .catch(error => {
        setCustomerOptions([]);
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const listAllCustomersByCustomerGroup = useCallback(async (customerGroupId: string, url: string) => {
    setLoading(true);
    await customerService
      .listDynamicallyByCustomerGroup(customerGroupId, url)
      .then(result => {
        if (result) {
          setCustomers(result.data);
          const options: IOption[] = result.data.map(customerToOption);
          const isCustomerOnArray = options.find(opt => opt.value === customer?.id);
          if (customer?.id && !isCustomerOnArray) {
            options.push(customerToOption(customer));
          }
          setCustomerOptions(options);
        }
      })
      .catch(error => {
        setCustomerOptions([]);
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleListCustomersDynamically = useCallback(
    async (filter = '') => {
      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        const url = `perPage=${perPage}&currentPage=${
          currentPage + 1
        }&orderBy=${orderField}&orderDirection=${orderDirection}&filter=${filter}`;

        await listAllCustomers(url);
      }, 100);
    },
    [customerGroupId, customerDefault],
  );

  const handleValidateHubSpotDealId = async () => {
    let resultHubSpot: IFormErrorResult | null = null;
    setErrorsHubSpot(null);

    const data = { hubSpotId, orderTypeId: orderType?.id };
    resultHubSpot = await validation.validate(schemaHubSpot, data || {});

    if (!resultHubSpot.isValid) {
      setErrorsHubSpot(resultHubSpot.errors);
      return;
    }

    setValidateHubSpotDealIdLoading(true);
    await validateHubSpotDealId(hubSpotId, orderType?.id || 1, state.seller?.id || '')
      .then(response => {
        setCustomer(response.customer);
        setCustomerOption(customerToOption(response.customer));
        setCustomerGroupId(response.customer?.customerGroupId || '');
        setSeller(response.seller);
        setPipeline(response.pipeline);
        setHubSpotDealIdIsValid(true);

        const options: IOption[] = [customerToOption(response.customer)];
        setCustomerOptions(options);
        setCustomerDefault(response.customer);
      })
      .catch(error => {
        toastr.error(error.message || 'Erro ao consultar pedido no HubSpot, contate o suporte.');
      })
      .finally(() => {
        setValidateHubSpotDealIdLoading(false);
      });
  };

  const listBranchOffice = async () => {
    setLoading(true);
    await branchOfficeService
      .listAll(`perPage=100&currentPage=1&orderBy=name&orderDirection=ASC`)
      .then(result => {
        setBranchOffices(result.data);
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const clearForm = () => {
    setCustomer(null);
    setSeller(null);
    setHubSpotDealIdIsValid(false);
    setOrderType(null);
    setHubSpotId('');
    setCustomerOption(null);
  };

  const customerCreated = (customerParam: ICustomer) => {
    setCustomer({ ...customerParam });
    setCustomerOption(customerToOption(customerParam));
    setIsCustomerDialogOpen(false);
  };

  const handleLoadData = async () => {
    await Promise.all([listBranchOffice(), listOrderTypes()]);

    const id = params.id;
    if (id && validateUuId(id)) {
      listOrder(id);
    }
  };

  useEffect(() => {
    handleLoadData();
  }, []);

  useEffect(() => {
    if (location.search.includes('hubspotId')) {
      const hubspotId = new URLSearchParams(location.search).get('hubspotId');
      if (hubspotId) {
        setHubSpotId(hubspotId);
      }
    }
  }, []);

  return (
    <>
      {orderParamsLoading && <BackdropCustom />}

      <>
        {validateHubSpotDealIdLoading && <BackdropCustom />}
        {integrateHubSpot ? (
          <>
            <Grid container spacing={2} sx={{ marginTop: '4px' }}>
              {isNew && (
                <Grid item xs={12} md={6}>
                  <FormControl size="small" sx={{ width: '100%' }} required>
                    <InputLabel id="branch-office-label">Filial</InputLabel>
                    <Select
                      labelId="branch-office-label"
                      id="branch-office"
                      value={branchOffice?.id || ''}
                      label="Filial"
                      size="small"
                      required
                      onChange={e => {
                        const branchOffice = branchOffices.find(b => b.id === e.target.value) || null;
                        setBranchOffice(branchOffice);
                      }}
                    >
                      {branchOffices.map(bo => (
                        <MenuItem key={bo.id} value={bo.id}>
                          {bo.code} - {bo.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={isNew ? 3 : 6}>
                <Autocomplete
                  disabled={!!order?.id || hubSpotDealIdIsValid}
                  options={orderTypes}
                  fullWidth
                  value={orderType}
                  onChange={(_, newValue) => {
                    setOrderType(newValue);
                  }}
                  isOptionEqualToValue={(option, value) => {
                    return option?.description === value?.description || true;
                  }}
                  getOptionLabel={option => (option?.description ? `${option.description}` : '')}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Tipo de Pedido"
                      size="small"
                      error={Boolean(errorsHubSpot?.orderTypeId)}
                      helperText={errorsHubSpot?.orderTypeId}
                      disabled={!!order?.id || hubSpotDealIdIsValid}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={isNew ? 3 : 6}>
                <div style={{ display: 'flex', marginBottom: '8px' }}>
                  <TextField
                    sx={{ width: '100%' }}
                    size="small"
                    label="ID Negociação HubSpot"
                    value={hubSpotId}
                    onChange={e => setHubSpotId(e.target.value)}
                    autoComplete="off"
                    error={Boolean(errorsHubSpot?.hubSpotId)}
                    helperText={errorsHubSpot?.hubSpotId}
                    disabled={!!order?.id || hubSpotDealIdIsValid}
                  />

                  {isNew && (
                    <ButtonCustom
                      variant="contained"
                      size="small"
                      sx={{ height: '35px', marginLeft: '8px' }}
                      onClick={handleValidateHubSpotDealId}
                      disabled={hubSpotDealIdIsValid}
                    >
                      Pesquisar
                    </ButtonCustom>
                  )}
                </div>
              </Grid>
            </Grid>

            {seller && (
              <>
                {adminResource && isNew && (
                  <TextField
                    size="small"
                    value={seller.name}
                    autoComplete="off"
                    disabled
                    sx={{ width: '100%', marginBottom: '8px', marginTop: '4px' }}
                  />
                )}

                {location.pathname.includes('/steper') && (
                  <Grid item xs={12} md={6} sx={{ marginTop: '4px' }}>
                    <AsyncSearch
                      disabled={!!order?.totalValue && order?.totalValue !== 0 && order.orderStatusId !== 1}
                      options={customerOptions}
                      setOptions={setCustomerOptions}
                      option={customerOption}
                      setOption={handleSetCustomer}
                      asyncSearch={handleListCustomersDynamically}
                      loading={loading}
                      error={Boolean(errors?.value)}
                      errorMessage={errors?.value || null}
                      label="Cliente"
                    />
                  </Grid>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {adminResource ? (
              <>
                {!integrateHubSpot && <br />}
                <AsyncSearch
                  options={sellerOptions}
                  setOptions={setSellerOptions}
                  option={sellerOption}
                  setOption={handleSetSeller}
                  asyncSearch={listSellers}
                  loading={loading}
                  error={Boolean(errorsSeller?.name)}
                  errorMessage={errorsSeller?.name || null}
                  label="Representantes"
                  disabled={!!order?.totalValue && order?.totalValue !== 0}
                />
                <br />
              </>
            ) : (
              ''
            )}

            <AsyncSearch
              disabled={!!order?.totalValue && order?.totalValue !== 0}
              options={customerOptions}
              setOptions={setCustomerOptions}
              option={customerOption}
              setOption={handleSetCustomer}
              asyncSearch={handleListCustomersDynamically}
              loading={loading}
              error={Boolean(errors?.corporateDocument)}
              errorMessage={errors?.corporateDocument || null}
              label="Cliente"
            />
            <br />
            {enableProspect && isNew && (
              <Button style={{ textTransform: 'none' }} onClick={() => setIsCustomerDialogOpen(true)}>
                Adicionar Prospect
              </Button>
            )}
            {enableProspect && customer?.isProspect && (
              <Button style={{ textTransform: 'none' }} onClick={() => setIsCustomerDialogOpen(true)}>
                Editar Prospect
              </Button>
            )}
            <CustomerDialogForm
              open={isCustomerDialogOpen}
              onClose={() => setIsCustomerDialogOpen(false)}
              customerCreated={customerCreated}
              customerId={customer?.isProspect ? customer?.id : undefined}
            />
          </>
        )}
      </>
    </>
  );
});

export default OrderCustomerForm;
